import { getSortColumn, setSortColumn } from '../../../../store/utils';
import { Control, ControlContainer } from '../../../Controls/styled';
import { ButtonHeader } from '../../../Header/Style';
import SVG from '../../../SVG';
import Table from '../../../Table';
import useValidityController from '../useValidityController';

const useStatusController = () => {
  const {
    getModal,
    setModal,
    currentRoute,
    enqueueSnackbar,
    Can,
    filterProducts,
    getFormMessage,
    validateForm,
    getValidity,
    setValidity,
    autocompleteProducts,
    ability,
    filterBranches,
    createValidityStatus,
    updateValidity,
    updateValidityStatus,
    filterValidities,
    deleteValidityStatus: deleteValiditiesStatusAction,
    autocompleteValidityStatus,
    filterValidityStatus,
  } = useValidityController();

  const searchFilter = getValidity('filters.status.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setValidity('filters.status.filter', filter);

  const statuses = getValidity('filters.status.data.data');

  const editStatus = ({ id, name, color }: ValidityStatus) => {
    setModal('single.updateStatus', true);
    setValidity('forms.status.update', {
      id,
      name,
      color,
    });
  };

  const checkStatusIds = getValidity('selecteds.status');
  const hasCheckedStatus = checkStatusIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setValidity('filters.status.filter', {
      ...getValidity('filters.status.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setValidity('selecteds.status', []);
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const StatusControls = () => {
    return currentRoute.includes('/tools/validity/status') ? (
      <>
        <Can I="create" a="ValidityStatus">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createStatus', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVO STATUS
            </ButtonHeader>
          )}
        </Can>
        <ControlContainer>
          <Can I="delete" a="ValidityStatus">
            {() => (
              <Control
                active={hasCheckedStatus}
                disabled={!hasCheckedStatus}
                onClick={() => {
                  setModal('single.deleteStatus', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedStatus ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>
          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterStatus', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };

  const setCheckStatusIds = (selectedStatusIds: string[]) =>
    setValidity('selecteds.status', selectedStatusIds);

  const checkStatusId = (validityId: string) => {
    const newcheckIds = checkStatusIds.includes(validityId)
      ? checkStatusIds.filter((id) => id !== validityId)
      : [...checkStatusIds, validityId];
    setCheckStatusIds(newcheckIds);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const validityId = statuses[index].id;
    checkStatusId(validityId);
  };

  const allValidityChecks = statuses.every(({ id }) =>
    checkStatusIds.includes(id),
  );

  const validityCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckStatusIds(statuses.map(({ id }) => id));
    } else {
      setCheckStatusIds([]);
    }
  };

  const total = getValidity('filters.status.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });
  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };

  const deleteStatusLoadings = getValidity('loadings.deleteStatus');

  const setDeleteStatusLoadings = (setDeleteStatusLoadings: boolean[]) =>
    setValidity('loadings.deleteStatus', setDeleteStatusLoadings);

  const deleteStatus = (statusIds: string[] = checkStatusIds) =>
    new Promise((resolve, reject) => {
      {
        const length = statusIds.length;
        const indexes = statusIds.map((statusId) =>
          statuses.findIndex(({ id }) => statusId === id),
        );
        setDeleteStatusLoadings(
          statuses.map((_, i) =>
            indexes.includes(i) ? true : deleteStatusLoadings[i],
          ),
        );
        deleteValiditiesStatusAction(statusIds)
          .then(() => {
            const newCheckIds = checkStatusIds.filter(
              (id) => !statusIds.includes(id),
            );
            setCheckStatusIds(newCheckIds);
            setValidity(`filters.status.data.data`, [
              ...statuses.filter(({ id }) => !statusIds.includes(id)),
            ]);
            setTimeout(() => {
              filterValidityStatus();
            }, 1000);
            resolve(true);
            enqueueSnackbar(
              length === 1
                ? 'Status excluído com sucesso'
                : 'Status foram excluídos com sucesso',
              {
                variant: 'success',
              },
            );
          })
          .catch(() => {
            reject(false);
            enqueueSnackbar(
              length === 1
                ? 'Houve uma falha ao excluir o status'
                : 'Houve uma falha ao excluir os status',
              {
                variant: 'error',
              },
            );
          })
          .finally(() => {
            setDeleteStatusLoadings(
              statuses.map((_, i) =>
                indexes.includes(i) ? false : deleteStatusLoadings[i],
              ),
            );
          });
      }
    });

  return {
    getModal,
    setModal,
    StatusControls,
    filterProducts,
    getFormMessage,
    validateForm,
    getValidity,
    setValidity,
    enqueueSnackbar,
    autocompleteProducts,
    searchFilter,
    setSearchFilter,
    statuses,
    Can,
    ability,
    editStatus,
    filterBranches,
    createValidityStatus,
    updateValidity,
    updateValidityStatus,
    filterValidities,
    onLineClickHandler,
    allValidityChecks,
    validityCheckAll,
    checkStatusIds,
    checkStatusId,
    TableControl,
    deleteStatusLoadings,
    deleteStatus,
    autocompleteValidityStatus,
    getSortField,
    setSortField,
    filterValidityStatus,
  };
};

export default useStatusController;
